import { Select, Spin } from 'antd';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LoadingOutlined } from '@ant-design/icons';
import {
  getDeviceGroupsByPageV2,
  useGetDeviceGroupsByPageV2,
} from '../../../../hooks/socket/cameras';
import { debounce } from 'lodash';

const mergeData = (prev, current) => {
  return {
    ...current,
    deviceGroups: [
      ...(prev?.deviceGroups || []),
      ...(current?.deviceGroups || []),
    ],
  };
};

export const GroupSelector = ({
  namespace,
  additionalOptions,
  onSelect,
  selectedId,
}) => {
  const [isDebounce, setIsDebounce] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [pages, setPages] = useState([]);
  const timeRequestRef = useRef(Date.now());
  const searchRef = useRef('');

  const isLoading = isDebounce || isPageLoading;

  const data = useMemo(() => {
    return pages.reduce(mergeData, {});
  }, [pages]);

  const fetchGroup = useCallback(
    ({ page, name }) => {
      setIsPageLoading(true);
      return getDeviceGroupsByPageV2({
        page,
        size: 10,
        name,
        namespace,
        timeRequest: timeRequestRef.current,
      }).then(
        (res) => {
          setIsPageLoading(false);

          return res;
        },
        () => {
          setIsPageLoading(false);
        }
      );
    },
    [namespace]
  );

  // useEffect(() => {
  //   fetchGroup({
  //     page: 0,
  //     name: '',
  //   }).then((res) => {
  //     setPages([res]);
  //   });
  // }, [namespace]);

  const handleSearch = useMemo(
    () =>
      debounce(
        (value) => {
          searchRef.current = value;
          setIsDebounce(false);
          const timeRequest = Date.now();
          timeRequestRef.current = timeRequest;

          fetchGroup({
            page: 0,
            name: value || '',
          }).then((res) => {
            setPages([res]);
          });
        },
        500,
        { leading: false, trailing: true }
      ),
    [fetchGroup]
  );

  const fetchNextPage = useCallback(() => {
    fetchGroup({
      page: pages.length,
      name: searchRef.current,
    }).then((res) => {
      setPages((prev) => [...prev, res]);
    });
  }, [fetchGroup, pages.length]);

  const handleScroll = useMemo(
    () => debounce(fetchNextPage, 200, { leading: true, trailing: true }),
    [fetchNextPage]
  );

  const onScroll = (e) => {
    if (isLoading) return;

    if (data.deviceGroups.length >= data.total) {
      console.log('end');
      return;
    }

    const isBottom =
      e.target.scrollTop + e.target.clientHeight + 10 >= e.target.scrollHeight;

    if (isBottom) {
      handleScroll(e);
    }
  };

  const onSearch = (value) => {
    setIsDebounce(true);
    handleSearch(value);
  };

  const options = useMemo(() => {
    const mainOptions =
      data?.deviceGroups?.map((group) => ({
        label: group?.name ?? '',
        value: group?.id ?? '',
      })) || [];

    const list = (additionalOptions || []).concat(mainOptions);

    return Array.from(
      new Map(
        list
          .filter((item) => item && item.value !== undefined && item.value !== null)
          .map((item) => [item.value, item])
      ).values()
    );
  }, [data, additionalOptions]);

  const [key, setKey] = useState(0);

  return (
    <Select
      key={key}
      style={{ width: '100%' }}
      value={selectedId}
      onChange={(_v, option) => {
        onSelect({
          id: option.value,
          name: option.label,
        });
      }}
      placeholder="Select group"
      showSearch
      filterOption={false}
      onSearch={onSearch}
      options={options}
      onPopupScroll={onScroll}
      listItemHeight={32}
      listHeight={300}
      getPopupContainer={(trigger) => trigger.parentElement}
      allowClear
      dropdownRender={(menu) => {
        return (
          <Spin
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
            spinning={isLoading}
          >
            {menu}
          </Spin>
        );
      }}
      onFocus={() => {
        // retain data if no search was done last time
        if (searchRef.current || pages.length === 0) {
          timeRequestRef.current = Date.now();

          fetchGroup({
            page: 0,
            name: '',
          }).then((res) => {
            setPages([res]);
          });
        }
      }}
      onBlur={() => {
        setKey((prev) => (prev + 1) % 5);
      }}
    ></Select>
  );
};
